import React, {useEffect} from "react";
import {Button, Card, CardContent, Grid, Toolbar, Typography} from "@material-ui/core";
import {Field, Form} from "react-final-form";
import TextField from "../../../../general/text_field";
import DownloadButton from "../../../../general/download_button";
import {pocPrimary, useGlobalStyles} from "../../../utils/styles";
import {Alert} from "@material-ui/lab";
import {getCatalogSyncHistories, utilTriggerWorkflowForPartner} from "../../../utils/utils";
import {identity} from "./setting_modifier";
import EnhancedErrorDialog from "../../../components/EnhancedErrorDialog";
import cronstrue from "cronstrue";
import EnhancedCircularLoader from "../../../components/EnhancedCircularLoader";
import moment from "moment";

export default function ChannelModifier(props) {
    const {partners, retailers} = props;
    const globalClasses = useGlobalStyles();

    let properties = props.properties || [];

    const [error, setError] = React.useState({open: false, errorHeader: "", errorMessage: ""});
    const [schedules, setSchedules] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [firingSync, setFiringSync] = React.useState(false);

    const fireSync = async (connectorId) => {
        setFiringSync(true);
        const response = await utilTriggerWorkflowForPartner(connectorId);
        if (!response.success) {
            setError({open: true, errorHeader: "Error sending manual sync", errorMessage: response.error});
        }
        setFiringSync(false);
    }

    useEffect(() => {
        const getSchedules = async () => {
            let response = await getCatalogSyncHistories();
            if(response.code !== 200) {
                setError({open: true, errorHeader: "Error getting schedules", errorMessage: response.error});
            }
            else {
                setSchedules(response.data);
            }
        }

        const init = async () => {
            setLoading(true);
            await getSchedules();
            setLoading(false);
        }

        init();
    }, []);

    return (
        <Grid container style={{gap: "12px"}}>
            <Grid container item md={12} style={{gap: "12px", flexDirection: "column"}}>
                <Card>
                    <Toolbar
                        className='lbtoolbar'
                        style={{background: pocPrimary}}
                    >
                        General Settings
                    </Toolbar>

                    <Alert severity={"info"} style={{margin: "1em 1em 0 1em"}}>
                        <Typography variant={"body1"} style={{textAlign: "left"}}>
                            The settings configured in this section will be applied to all channels. If you would like
                            to configure settings on a per-channel basis, this can be done in the channel specific
                            space, below. Please note: a schedule is required in order to automatically list products.
                            Any settings applied on a per-channel basis will override what has been applied in General
                            Settings.
                        </Typography>
                    </Alert>

                    <CardContent>
                        <Form onSubmit={props.saveRow} initialValues={props.state.settings}
                              render={({handleSubmit, pristine, invalid, values}) => {
                                  return (
                                      <form onSubmit={handleSubmit}
                                            style={{display: 'flex', flexDirection: 'column', maxWidth: '900px'}}>
                                          {properties.filter(prop => !prop.partnerOnly).map(r => <div key={r.name} style={{display: 'flex'}}>
                                              <Field style={{flex: 1}} parse={identity} component={TextField} {...r}/>
                                          </div>)}
                                          <div style={{
                                              textAlign: 'left', marginBottom: '.5em',
                                              marginTop: '0.5em'
                                          }}>
                                              <DownloadButton onClick={handleSubmit}
                                                              disabled={pristine || invalid}
                                                              progressclasses={{
                                                                  colorPrimary: globalClasses.progressBar,
                                                                  barColorPrimary: globalClasses.progressBarBackground
                                                              }}
                                                              className={(pristine || invalid) ? globalClasses.disabledButton : globalClasses.actionButton}
                                              >
                                                  Save
                                              </DownloadButton>
                                          </div>
                                      </form>
                                  );
                              }}/>
                    </CardContent>
                </Card>

                <>
                    {retailers.filter(retailer => retailer.Marketplace).map((retailer, index) => {
                        const profiles = props.profiles.filter(p => p.Partner === retailer.Id && p.Shared !== undefined);
                        const schedule = schedules.find(s => s.CoId === retailer.Id);
                        return (
                            <Card key={index}>
                                <Toolbar
                                    className='lbtoolbar'
                                    style={{background: pocPrimary}}
                                >
                                    {partners.find(p => p.Id === retailer.Id)?.CompanyName ?? "Unknown"} Settings
                                </Toolbar>

                                <Alert severity={"info"} style={{margin: "1em 1em 0 1em"}}>
                                    <Typography variant={"body1"} style={{textAlign: "left"}}>
                                        These settings will override the general settings configured. Please note: a
                                        schedule is required in order to automatically list products.
                                    </Typography>
                                </Alert>

                                <CardContent style={{display: "flex", flexDirection: "column", gap: "20px"}}>
                                    <Grid container>
                                        <Grid container style={{flexDirection: "column", textAlign: "left"}}>
                                            <Typography style={{color: "black", fontWeight: "bold", fontSize: "1rem"}}>
                                                Scheduling
                                            </Typography>

                                            <Typography style={{color: "#c0c0c0", paddingBottom: "10px"}}>
                                                Below is the schedule for frequency of when products are being sent to {partners.find(p => p.Id === retailer.Id)?.CompanyName ?? "Unknown"}.
                                            </Typography>
                                        </Grid>

                                        <Grid>
                                            {loading ? (
                                                <Grid style={{padding: "20px"}}>
                                                    <EnhancedCircularLoader styles={{width: "40px", height: "40px", margin: "10px"}}/>
                                                </Grid>
                                            ) : (
                                                <Typography style={{padding: "5px"}}>
                                                    <b>Runs:{" "}</b>
                                                    {schedule?.Schedule
                                                        ? cronstrue.toString(schedules.find(s => s.CoId === retailer.Id)?.Schedule ?? "* * * * *")
                                                        : "No schedule has been set yet."
                                                    }
                                                </Typography>
                                            )}
                                        </Grid>

                                        <Grid container style={{flexDirection: "row", padding: "5px", justifyContent: "flex-start"}}>
                                            <Button
                                                variant={"contained"}
                                                color={"primary"}
                                                disabled={!schedule?.Schedule || firingSync}
                                                onClick={() => fireSync(retailer.Id)}
                                            >
                                                {firingSync ? (
                                                    <EnhancedCircularLoader styles={{height: "20px", width: "20px"}}/>
                                                ) : "Go Faster"}
                                            </Button>
                                        </Grid>

                                        <Grid container style={{flexDirection: "column", textAlign: "left", padding: "5px"}}>
                                            <Typography style={{color: "black", fontWeight: "bold"}}>
                                                Last Import
                                            </Typography>

                                            <Typography style={{color: "#c0c0c0", paddingBottom: "10px"}}>
                                                {
                                                    props.lastImports[retailer.Id]
                                                        ?
                                                        <>
                                                            {props.lastImports[retailer.Id]?.Description ?? "Unknown"} on {moment(props.lastImports[retailer.Id]?.CreateDate).format("MM/DD/YYYY")}
                                                        </>
                                                        : "No import has been made yet."
                                                }
                                            </Typography>
                                        </Grid>
                                    </Grid>

                                    <Form
                                        onSubmit={(e) => {
                                            const params = {
                                                ...e,
                                                AccountNumber: retailer.Id,
                                            };
                                            props.saveRow(params);
                                        }}
                                        initialValues={props.state.settings.PartnerProperties?.find(p => p.PartnerCoId === retailer.Id) ?? {}}
                                        render={({handleSubmit, pristine, invalid, values}) => {
                                            return (
                                                <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column', maxWidth: '900px', gap: "20px"}}>
                                                    {
                                                        properties.map(r => (
                                                            <div key={r.name} style={{display: 'flex'}}>
                                                                <Field
                                                                    style={{flex: 1}}
                                                                    parse={identity}
                                                                    {...r}
                                                                    component={(innerProps) => {
                                                                        return (
                                                                            <>
                                                                                {
                                                                                    r.name === "POCAutoIgnoreUpdatedFields"
                                                                                        ? r.component({
                                                                                            ...innerProps,
                                                                                            options: (props.groupedAttributes[retailer.Id] ?? []),
                                                                                        })
                                                                                        : r.component({...innerProps, profiles, lastImport: props.lastImports[retailer.Id], retailerId: retailer.Id})
                                                                                }
                                                                            </>
                                                                        );
                                                                    }}
                                                                />
                                                            </div>
                                                        ))
                                                    }
                                                    <div style={{
                                                        textAlign: 'left',
                                                        display: "flex",
                                                        gap: "20px",
                                                    }}>
                                                        <DownloadButton onClick={handleSubmit}
                                                                        disabled={pristine || invalid}
                                                                        progressclasses={{
                                                                            colorPrimary: globalClasses.progressBar,
                                                                            barColorPrimary: globalClasses.progressBarBackground
                                                                        }}
                                                                        className={(pristine || invalid) ? globalClasses.disabledButton : globalClasses.actionButton}
                                                        >
                                                            Save
                                                        </DownloadButton>
                                                    </div>
                                                </form>
                                            );
                                        }}/>
                                </CardContent>
                            </Card>
                        );
                    })}
                </>
            </Grid>

            <EnhancedErrorDialog open={error.open} onClose={() => setError({...error, open: false})}
                                 errorHeader={error.errorHeader} errorMessage={error.errorMessage}/>
        </Grid>
    );
}
